import { useState, useEffect, useRef } from "react";
import "./CookieBanner.css";

const COOKIE_CONSENT_KEY = "undrive_cookie_consent";

function CookieBanner({ lang }) {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const bannerRef = useRef(null);

  const t = {
    DE: {
      title: "Cookie-Einstellungen",
      text: "Wir nutzen Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.",
      necessary: "Notwendig",
      necessaryDesc: "Diese Cookies sind für die Grundfunktionen der Website erforderlich.",
      analytics: "Analyse",
      analyticsDesc: "Helfen uns zu verstehen, wie Besucher mit der Website interagieren.",
      acceptAll: "Alle akzeptieren",
      acceptNecessary: "Nur notwendige",
      settings: "Einstellungen",
      save: "Speichern",
      privacy: "Datenschutz"
    },
    EN: {
      title: "Cookie Settings",
      text: "We use cookies to provide you with the best possible experience on our website.",
      necessary: "Necessary",
      necessaryDesc: "These cookies are required for basic website functionality.",
      analytics: "Analytics",
      analyticsDesc: "Help us understand how visitors interact with the website.",
      acceptAll: "Accept all",
      acceptNecessary: "Necessary only",
      settings: "Settings",
      save: "Save",
      privacy: "Privacy"
    },
    FR: {
      title: "Paramètres des cookies",
      text: "Nous utilisons des cookies pour vous offrir la meilleure expérience possible sur notre site.",
      necessary: "Nécessaires",
      necessaryDesc: "Ces cookies sont requis pour les fonctions de base du site.",
      analytics: "Analyse",
      analyticsDesc: "Nous aident à comprendre comment les visiteurs interagissent avec le site.",
      acceptAll: "Tout accepter",
      acceptNecessary: "Nécessaires uniquement",
      settings: "Paramètres",
      save: "Enregistrer",
      privacy: "Confidentialité"
    }
  }[lang] || {
    title: "Cookie-Einstellungen",
    text: "Wir nutzen Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.",
    necessary: "Notwendig",
    necessaryDesc: "Diese Cookies sind für die Grundfunktionen der Website erforderlich.",
    analytics: "Analyse",
    analyticsDesc: "Helfen uns zu verstehen, wie Besucher mit der Website interagieren.",
    acceptAll: "Alle akzeptieren",
    acceptNecessary: "Nur notwendige",
    settings: "Einstellungen",
    save: "Speichern",
    privacy: "Datenschutz"
  };

  const [consent, setConsent] = useState({
    necessary: true,
    analytics: false
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
    const parsed = JSON.parse(savedConsent);
    setConsent(parsed);
    if (parsed.analytics) {
      initGA4();
    }
  }, []);

  // Allow reopening from footer link
  useEffect(() => {
    const handleReopen = () => {
      const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (saved) setConsent(JSON.parse(saved));
      setShowDetails(true);
      setShowBanner(true);
    };
    window.addEventListener("reopenCookieSettings", handleReopen);
    return () => window.removeEventListener("reopenCookieSettings", handleReopen);
  }, []);

  // Focus trap for dialog
  useEffect(() => {
    if (!showBanner || !bannerRef.current) return;

    const banner = bannerRef.current;
    const focusableSelector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = banner.querySelectorAll(focusableSelector);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    firstFocusable?.focus();

    const trapFocus = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable?.focus();
      } else if (!e.shiftKey && document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable?.focus();
      }
    };

    document.addEventListener('keydown', trapFocus);
    return () => document.removeEventListener('keydown', trapFocus);
  }, [showBanner, showDetails]);

  const saveConsent = (consentData) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setShowBanner(false);
    if (consentData.analytics) {
      initGA4();
    }
  };

  const initGA4 = () => {
    const gaId = process.env.REACT_APP_GA4_ID;
    if (!gaId || document.getElementById('ga4-script')) return;
    const script = document.createElement('script');
    script.id = 'ga4-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    function gtag(){ window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', gaId, { anonymize_ip: true });
  };

  const handleAcceptAll = () => {
    const fullConsent = { necessary: true, analytics: true };
    setConsent(fullConsent);
    saveConsent(fullConsent);
  };

  const handleAcceptNecessary = () => {
    const minConsent = { necessary: true, analytics: false };
    setConsent(minConsent);
    saveConsent(minConsent);
  };

  const handleSaveSettings = () => {
    saveConsent(consent);
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-overlay" data-testid="cookie-banner" role="dialog" aria-modal="true" aria-label={t.title}>
      <div className="cookie-banner" ref={bannerRef}>
        <div className="cookie-header">
          <h3 className="cookie-title"><span aria-hidden="true">🍪</span> {t.title}</h3>
        </div>

        {!showDetails ? (
          <>
            <p className="cookie-text">{t.text}</p>
            <div className="cookie-buttons">
              <button
                className="cookie-btn primary"
                onClick={handleAcceptAll}
                data-testid="cookie-accept-all"
              >
                {t.acceptAll}
              </button>
              <button
                className="cookie-btn secondary"
                onClick={handleAcceptNecessary}
                data-testid="cookie-accept-necessary"
              >
                {t.acceptNecessary}
              </button>
              <button
                className="cookie-btn text"
                onClick={() => setShowDetails(true)}
                data-testid="cookie-settings"
              >
                {t.settings}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="cookie-options">
              <div className="cookie-option">
                <div className="cookie-option-header">
                  <label className="cookie-label">
                    <input
                      type="checkbox"
                      checked={consent.necessary}
                      disabled
                      className="cookie-checkbox"
                      aria-label={t.necessary}
                    />
                    <span className="cookie-option-name">{t.necessary}</span>
                  </label>
                  <span className="cookie-required">{lang === "DE" ? "Erforderlich" : lang === "EN" ? "Required" : "Requis"}</span>
                </div>
                <p className="cookie-option-desc">{t.necessaryDesc}</p>
              </div>

              <div className="cookie-option">
                <div className="cookie-option-header">
                  <label className="cookie-label">
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                      className="cookie-checkbox"
                      data-testid="cookie-analytics-checkbox"
                      aria-label={t.analytics}
                    />
                    <span className="cookie-option-name">{t.analytics}</span>
                  </label>
                </div>
                <p className="cookie-option-desc">{t.analyticsDesc}</p>
              </div>
            </div>

            <div className="cookie-buttons">
              <button
                className="cookie-btn primary"
                onClick={handleSaveSettings}
                data-testid="cookie-save"
              >
                {t.save}
              </button>
              <button
                className="cookie-btn text"
                onClick={() => setShowDetails(false)}
              >
                ← {t.title}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CookieBanner;
